import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks';
import { getUserCreditStatus, formatCredits, formatCreditsCurrency, getStatusColor, getStatusBgColor } from '../../utils/limitChecks';
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ArrowTrendingUpIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';

interface CreditUsageWidgetProps {
  userId?: number;
  orgId?: number;
  title?: string;
  showTrend?: boolean;
  compact?: boolean;
  className?: string;
  onLimitExceeded?: () => void;
}

interface CreditStatusData {
  current_usage: number;
  remaining: number;
  limit_value: number;
  usage_percentage: number;
  status: 'normal' | 'warning' | 'critical' | 'exceeded';
}

const CreditUsageWidget: React.FC<CreditUsageWidgetProps> = ({
  userId,
  orgId,
  title = 'Credit Usage',
  showTrend = true,
  compact = false,
  className = '',
  onLimitExceeded,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const [creditStatus, setCreditStatus] = useState<CreditStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Use provided userId/orgId or fall back to current user
  const effectiveUserId = userId || user?.user_id;
  const effectiveOrgId = orgId || user?.organization_id;

  useEffect(() => {
    const fetchCreditStatus = async () => {
      if (!effectiveUserId || !effectiveOrgId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const status = await getUserCreditStatus(effectiveUserId, effectiveOrgId);
        setCreditStatus(status);
        setLastUpdated(new Date());
        
        // Trigger callback if limit is exceeded
        if (status.status === 'exceeded' && onLimitExceeded) {
          onLimitExceeded();
        }
      } catch (error) {
        console.error('Failed to fetch credit status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCreditStatus();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchCreditStatus, 30000);
    return () => clearInterval(interval);
  }, [effectiveUserId, effectiveOrgId, onLimitExceeded]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'normal':
        return <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
      case 'critical':
        return <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />;
      case 'exceeded':
        return <XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />;
      default:
        return null;
    }
  };

  const getStatusMessage = (status: string, usage_percentage: number) => {
    switch (status) {
      case 'normal':
        return 'Usage is within normal limits';
      case 'warning':
        return `${usage_percentage.toFixed(1)}% of limit used - approaching threshold`;
      case 'critical':
        return `${usage_percentage.toFixed(1)}% of limit used - critical threshold reached`;
      case 'exceeded':
        return 'Credit limit exceeded - actions may be blocked';
      default:
        return 'Status unknown';
    }
  };

  if (loading) {
    return (
      <div className={`bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6 ${className}`}>
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!creditStatus) {
    return null;
  }

  const { current_usage, remaining, limit_value, usage_percentage, status } = creditStatus;

  if (compact) {
    return (
      <div className={`bg-white dark:bg-dark-surface rounded-lg border border-light-border dark:border-dark-border p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon(status)}
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {formatCredits(current_usage)} / {formatCredits(limit_value)}
            </span>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {usage_percentage.toFixed(1)}% used
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {formatCredits(remaining)} remaining
            </div>
          </div>
        </div>
        
        <div className="mt-2">
          <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`absolute top-0 left-0 h-full transition-all duration-500 ${
                status === 'exceeded' ? 'bg-red-500' :
                status === 'critical' ? 'bg-orange-500' :
                status === 'warning' ? 'bg-yellow-500' :
                'bg-green-500'
              }`}
              style={{ width: `${Math.min(usage_percentage, 100)}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
        {lastUpdated && (
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <ClockIcon className="w-3 h-3" />
            <span>Updated {lastUpdated.toLocaleTimeString()}</span>
          </div>
        )}
      </div>

      {/* Status Alert */}
      <div className={`mb-4 p-3 rounded-lg ${getStatusBgColor(status)}`}>
        <div className="flex items-center gap-2">
          {getStatusIcon(status)}
          <span className={`text-sm font-medium ${getStatusColor(status)}`}>
            {getStatusMessage(status, usage_percentage)}
          </span>
        </div>
      </div>

      {/* Usage Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Credits Used
          </span>
          <span className="text-sm font-medium text-primary-500">
            {usage_percentage.toFixed(1)}%
          </span>
        </div>
        
        <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full transition-all duration-500 ${
              status === 'exceeded' ? 'bg-red-500' :
              status === 'critical' ? 'bg-orange-500' :
              status === 'warning' ? 'bg-yellow-500' :
              'bg-green-500'
            }`}
            style={{ width: `${Math.min(usage_percentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Usage Details */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCredits(current_usage)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Used ({formatCreditsCurrency(current_usage)})
          </div>
        </div>
        
        <div className="text-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCredits(remaining)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Remaining ({formatCreditsCurrency(remaining)})
          </div>
        </div>
      </div>

      {/* Total Limit */}
      <div className="text-center p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
          Total Credit Limit
        </div>
        <div className="text-xl font-bold text-primary-600 dark:text-primary-400">
          {formatCredits(limit_value)}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          ({formatCreditsCurrency(limit_value)} USD equivalent)
        </div>
      </div>

      {/* Trend Information */}
      {showTrend && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-center gap-2 text-sm">
            <ArrowTrendingUpIcon className="w-4 h-4 text-blue-500" />
            <span className="font-medium text-blue-600 dark:text-blue-400">
              Usage Trend:
            </span>
            <span className="text-gray-700 dark:text-gray-300">
              {status === 'exceeded' ? 'Limit exceeded' :
               status === 'critical' ? 'Rapidly approaching limit' :
               status === 'warning' ? 'Moderate usage increase' :
               'Normal usage pattern'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditUsageWidget;
